import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { getCountryNames } from "Components/ReservationCalendar/PersonalInfoFields/PersonalInfoFields";
import { LanguageContext } from "MainApp/assets/utils/context/LanguageContext";
import useAxios from "MainApp/assets/utils/hooks/useAxios";
import styles from "./SuccessScreen.module.css";

const SuccessScreen = () => {
  const axiosInstance = useAxios();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("translation");
  const { language: languageCode } = useContext(LanguageContext);
  const searchParams = new URLSearchParams(location.search);
  const sessionId = searchParams.get("session_id");
  const bookingUUID = searchParams.get("booking_uuid");
  const userUUID = searchParams.get("user");
  const isFreeBooking = searchParams.get("free") === "1";
  const [bookingDetails, setBookingDetails] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [countryNames, setCountryNames] = useState({});

  const naLabel = t("generic.notAvailable");
  const vehicleLabel = t("generic.vehicle");

  useEffect(() => {
    const getSessionDetails = async () => {
      try {
        const endpoint = bookingUUID
          ? `/booking-success/${bookingUUID}`
          : `/stripe/session/${sessionId}`;

        const response = await axiosInstance.get(endpoint, {
          params: { userUUID },
        });

        if (response.data.success) {
          setBookingDetails(response.data.metadata);
        } else {
          setErrorMessage(
            response.data.message || t("payment.success.recoveryError")
          );
        }
      } catch (error) {
        setErrorMessage(t("payment.success.fetchError"));
      } finally {
        setIsLoading(false);
      }
    };

    if (bookingUUID || sessionId) {
      getSessionDetails();
    } else {
      setErrorMessage(t("payment.success.missingBookingId"));
      setIsLoading(false);
    }
  }, [axiosInstance, bookingUUID, sessionId, userUUID, t]);

  useEffect(() => {
    const fetchCountryNames = async () => {
      const countryList = await getCountryNames(languageCode);
      const countryMap = countryList.reduce((accumulator, country) => {
        accumulator[country.value] = country.label;
        return accumulator;
      }, {});
      setCountryNames(countryMap);
    };

    fetchCountryNames();
  }, [languageCode]);

  const safeValue = (value, defaultValue = naLabel) => {
    return value !== null && value !== undefined && value !== ""
      ? value
      : defaultValue;
  };

  const parseTuktukModels = (models) => {
    const fallbackModels = [{ id: "n/a", label: naLabel }];

    if (Array.isArray(models)) {
      const parsed = models
        .filter(Boolean)
        .map((model, index) => ({
          id: model.value ?? `model-${index}`,
          label: `${model.label || model.model || model.value || vehicleLabel} x${
            model.units || model.amount || 1
          }`,
        }));

      return parsed.length ? parsed : fallbackModels;
    }

    if (typeof models === "string" && models.trim() !== "") {
      try {
        return parseTuktukModels(JSON.parse(models));
      } catch (error) {
        console.error("Error al parsear tuktukModels:", error);
        return fallbackModels;
      }
    }

    if (models && typeof models === "object") {
      return parseTuktukModels([models]);
    }

    return fallbackModels;
  };

  return (
    <div className={styles.successScreen}>
      <div className={styles.successContainer}>
        {isLoading ? (
          <p>{t("payment.success.loading")}</p>
        ) : errorMessage ? (
          <div className={styles.errorContainer}>
            <FaExclamationTriangle className={styles.errorIcon} />
            <h1>{t("payment.success.errorTitle")}</h1>
            <p>{errorMessage}</p>
          </div>
        ) : (
          <>
            <FaCheckCircle className={styles.successIcon} />
            <h1>
              {isFreeBooking
                ? t("payment.success.freeBookingTitle")
                : t("payment.success.paidBookingTitle")}
            </h1>
            <p>
              {isFreeBooking
                ? t("payment.success.freeBookingMessage")
                : t("payment.success.paidBookingMessage")}
            </p>
            <div className={styles.bookingDetails}>
              <h2>{t("payment.success.detailsTitle")}</h2>
              <div className={styles.detailsGrid}>
                <div className={styles.detailSection}>
                  <h3>{t("payment.success.personalInfo")}</h3>
                  <p>
                    <strong>{t("payment.success.name")}:</strong>{" "}
                    {safeValue(bookingDetails?.clientName)}
                  </p>
                  <p>
                    <strong>{t("payment.success.email")}:</strong>{" "}
                    {safeValue(bookingDetails?.clientEmail)}
                  </p>
                  <p>
                    <strong>{t("payment.success.phone")}:</strong>{" "}
                    {safeValue(bookingDetails?.clientPhone)}
                  </p>
                  <p>
                    <strong>{t("payment.success.country")}:</strong>{" "}
                    {countryNames[bookingDetails?.clientCountry] ||
                      safeValue(bookingDetails?.clientCountry)}
                  </p>
                </div>
                <div className={styles.detailSection}>
                  <h3>{t("payment.success.tripDetails")}</h3>
                  <p>
                    <strong>{t("payment.success.adults")}:</strong>{" "}
                    {safeValue(bookingDetails?.adults)}
                  </p>
                  <p>
                    {Number(bookingDetails?.minorsTotal || 0) > 0 ? (
                      <>
                        <strong>{t("payment.success.minors")}:</strong>{" "}
                        {safeValue(bookingDetails?.minorsTotal)}
                      </>
                    ) : (
                      <strong>{t("payment.success.noMinors")}</strong>
                    )}
                  </p>
                </div>
                <div className={styles.detailSection}>
                  <h3>{t("payment.success.additionalInfo")}</h3>
                  <p>
                    <strong>{t("payment.success.vehiclesAndModels")}:</strong>
                  </p>
                  <ul className={styles.modelList}>
                    {parseTuktukModels(bookingDetails?.tuktukModels).map(
                      (model) => (
                        <li key={model.id}>{model.label}</li>
                      )
                    )}
                  </ul>
                </div>
                <div className={styles.detailSection}>
                  <h3>{t("payment.success.technicalIds")}</h3>
                  <p>
                    <strong>{t("payment.success.bookingTechnicalId")}:</strong>
                    <br /> {bookingDetails?.bookingUUID || naLabel}
                  </p>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate("/")}
              className={styles.backButton}
            >
              {t("payment.success.backHome")}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default SuccessScreen;
